import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Top10ArticlesColumns, Top10ArticleTableDataType } from "./types";
import { getCounter } from "../../lib/functions";
import { Button, Drawer, Image, Input, message, Table } from "antd";
import { deleteTop10Article, getTop10Articles } from "./action";
import SectionTitle from "../../Components/SectionTitle";
import { PiPlus } from "react-icons/pi";
import { CLOUD_URL } from "../../lib/env";
import DashPagination from "../../Components/DashPagination";
import DashModal from "../../Components/DashModal";
import AddNewTop10Article from "./Components/AddNewTop10Article";
import EditTop10ArticleTitle from "./Components/EditTop10Article";
import Top10ArticlesKeywordManager from "./Components/Top10ArticlesKeywordsManager";
import Top10ArticlesGeneralEdit from "./Components/Top10ArticlesGeneralEdit";
import EditDescription from "./Components/EditDescription";
import ManageTop10RichText from "./Components/ManageTop10RichText";
import EditTop10ArticleImage from "./Components/EditTop10ArticleImage";
import Top10BettingWebsitesSorter from "./Components/Top10BettingWebsitesSorter";
import SortedItemsSorter from "./Components/SortedItemsSorter";

const { Search } = Input;

type Props = {
  addNewArticle?: boolean;
  editArticleTitle?: boolean;
  editArticleDescription?: boolean;
  editKeywords?: boolean;
  editImage?: boolean;
  editTop10Wbsites?: boolean;
  addRichText?: boolean;
  manageRichText?: boolean;
  sortContent?: boolean;
  edit?: boolean;
  editRichText?: boolean;
};

const Top10Articles = (props: Props) => {
  const router = useNavigate();

  const [data, setData] = useState<Top10ArticleTableDataType[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (
    expanded: boolean,
    record: Top10ArticleTableDataType
  ) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("Top10Articles");
      setTotal(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const websites = await getTop10Articles(pageSize, page, query);
      const formatted = websites.map((wb) => ({
        id: wb.id,
        title: wb.MainInfo.Title,
        image: wb.MainInfo.Image,
        keywords: wb.MainInfo.Keywords,
        top10List: wb.Top10BettingWebsites,
      }));
      setData(formatted);
    } catch (error) {
      message.error("Failed to fetch websites");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    try {
      await deleteTop10Article(id);
      message.success("Article deleted");
      fetchData();
      fetchCount();
    } catch (error) {
      message.error("Failed to delete feature");
    }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Top 10 Articles" />
      <div className="flex justify-between items-center">
        <Link to={`/top10-articles/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PiPlus />}>
            Add Top 10 Article
          </Button>
        </Link>
        <Search
          allowClear
          placeholder="Search article..."
          className="w-1/5"
          onSearch={(value) => setQuery(value)}
        />
      </div>
      <Table<Top10ArticleTableDataType>
        rowKey="id"
        columns={Top10ArticlesColumns(page, pageSize, handleDelete)}
        dataSource={data}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <Image width={200} src={CLOUD_URL + "/" + record.image} />
          ),
          rowExpandable: () => true,
          onExpand: handleExpand,
          expandedRowKeys,
        }}
      />{" "}
      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      {props.addNewArticle && (
        <DashModal
          title="Add New Top 10 Article"
          width={800}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewTop10Article fetchData={fetchData} />
        </DashModal>
      )}
      {props.editArticleTitle && (
        <DashModal
          title="Edit top10 Article"
          width={1200}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditTop10ArticleTitle fetchData={fetchData} />
        </DashModal>
      )}
      {props.editKeywords && (
        <DashModal
          title="Edit Top10 Article"
          width={1200}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Top10ArticlesKeywordManager fetchData={fetchData} />
        </DashModal>
      )}
      {props.editArticleDescription && (
        <DashModal
          title="Edit Top10 Article Description"
          width={800}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditDescription fetchData={fetchData} />
        </DashModal>
      )}
      {props.manageRichText && (
        <DashModal
          title="Manage Top 10 Rich Texts"
          width={1200}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <ManageTop10RichText
            editRichText={props.editRichText}
            addNewRichText={props.addRichText}
            page={page}
            pageSize={pageSize}
          />
        </DashModal>
      )}
      {props.editImage && (
        <DashModal
          title="Edit Image"
          width={600}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditTop10ArticleImage fetchData={fetchData} />
        </DashModal>
      )}
      {props.editTop10Wbsites && (
        <DashModal
          title="Edit Betting Websites Sorting"
          width={900}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <Top10BettingWebsitesSorter fetchData={fetchData} />
        </DashModal>
      )}
      {props.sortContent && (
        <DashModal
          title="Edit Sorted Items"
          width={900}
          cancleLink={`/top10-articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <SortedItemsSorter fetchData={fetchData} />
        </DashModal>
      )}
      {props.edit && (
        <Drawer
          title="Edit Top 10 Articles"
          onClose={() =>
            router(`/top10-articles?page=${page + 1}&pageSize=${pageSize}`)
          }
          open={true}
        >
          <Top10ArticlesGeneralEdit page={page} pageSize={pageSize} />
        </Drawer>
      )}
    </div>
  );
};

export default Top10Articles;
